import React from "react";
import {
  Page,
  withLocalForm,
  FormGroup,
  NumberField,
  onMount,
  DatePicker,
  SubmitButton,
  withReload,
} from "components/common";
import {
  withContactSearch,
  withLineAmountTypes,
  withXeroErrors,
  AccountSelect,
} from "components";
import { Table, Button } from "reactstrap";
import { Control, actions, Fieldset, track } from "react-redux-form";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import { Trans } from "react-i18next";
import shortid from "shortid";
import { CurrencySymbol, formatCurrency } from "services/util";
import Select from "react-select";
import deepDiff from "deep-diff";
import { isNaN, find, sumBy, forEach, compact, pick, set } from "lodash";
import { flatten } from "flatulence";
import { NotificationManager } from "react-notifications";

export const Edit = ({
  match,
  LocalForm,
  state,
  organisation,
  handleAddNewLine,
  ContactSearch,
  handleRemoveLine,
  LineAmountTypes,
  accounts,
  taxRates,
  dispatch,
  handleLinesChanged,
  handleSubmit,
  XeroErrors,
  users,
}) => (
  <Page title="New Receipt">
    <XeroErrors />
    <LocalForm>
      <FormGroup label="Receipt from" model=".Contact">
        <ContactSearch model=".Contact" />
      </FormGroup>
      <FormGroup label="User" model=".User">
        <Control
          component={Select}
          model=".User"
          labelKey="Name"
          valueKey="UserID"
          options={users}
        />
      </FormGroup>
      <FormGroup label="Date" model=".Date">
        <DatePicker model=".Date" className="form-control" />
      </FormGroup>
      <FormGroup label="Reference" model=".Reference">
        <Control.text model=".Reference" updateOn="blur" />
      </FormGroup>
      <FormGroup label="Line item amounts are" model=".LineAmountTypes">
        <LineAmountTypes model=".LineAmountTypes" />
      </FormGroup>
      <Table>
        <thead>
          <tr>
            <th>
              <Trans>Description</Trans>
            </th>
            <th>
              <Trans>Quantity</Trans>
            </th>
            <th>
              <Trans>Unit Price</Trans>
            </th>
            <th className="text-right">
              <Trans>Amount</Trans> <CurrencySymbol currency={organisation.BaseCurrency} />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {state.LineItems.map(({ key, ...i }, idx) => (
            <Fieldset
              component="tr"
              key={key}
              model={track(".LineItems[]", { key })}
            >
              <td>
                <div className="d-flex flex-column">
                  <Control.text
                    model=".Description"
                    className="form-control mb-3"
                    updateOn="blur"
                  />
                  <div className="d-flex">
                    <FormGroup
                      label="Account"
                      model=".AccountCode"
                      className="account-select"
                    >
                      <AccountSelect
                        model=".AccountCode"
                        style={{ minWidth: "12rem" }}
                        accounts={accounts}
                        className="small"
                        changeAction={(m, v) => {
                          dispatch(actions.change(m, v ? v.Code : null));
                          dispatch(
                            actions.change(
                              `local.LineItems.[${idx}].TaxType`,
                              v ? v.TaxType : null
                            )
                          );
                        }}
                      />
                    </FormGroup>
                    <div className="mx-1" />
                    <FormGroup label="Tax Rate" model=".TaxType">
                      <Control
                        component={Select}
                        className="small"
                        model=".TaxType"
                        labelKey="Name"
                        valueKey="TaxType"
                        style={{ minWidth: "12rem" }}
                        options={taxRates}
                        simpleValue
                      />
                    </FormGroup>
                  </div>
                </div>
              </td>
              <td>
                <NumberField
                  model=".Quantity"
                  updateOn="blur"
                  className="form-control"
                />
              </td>
              <td>
                <NumberField
                  model=".UnitAmount"
                  updateOn="blur"
                  className="form-control"
                />
              </td>
              <td className="text-right">{formatCurrency(i.LineAmount)}</td>
              <td>
                <Button
                  onClick={() => handleRemoveLine(idx)}
                  color="link"
                  size="sm"
                >
                  <span className="fa fa-times" />
                </Button>
              </td>
            </Fieldset>
          ))}
        </tbody>
      </Table>
      <hr />
      <div className="d-flex justify-content-between align-items-start">
        <Button onClick={handleAddNewLine}>
          <Trans>
            <span className="fa fa-plus" /> Add a new line
          </Trans>
        </Button>
        <div>
          <table className="table table-sm borderless">
            <tbody>
              <tr>
                <td className="text-end">
                  <Trans>Subtotal</Trans>
                </td>
                <td className="text-right">{formatCurrency(state.SubTotal)}</td>
              </tr>
              <tr className={state.LineAmountTypes === "NoTax" ? "d-none" : ""}>
                <td className="text-end">
                  <Trans>VAT</Trans>
                </td>
                <td className="text-right">{formatCurrency(state.TotalTax)}</td>
              </tr>
              <tr className="border-top border-dark border-bottom font-weight-bold">
                <td className="text-end">
                  <Trans>Total</Trans>
                </td>
                <td className="text-right">{formatCurrency(state.Total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-between">
        <SubmitButton color="primary" onClick={() => handleSubmit(true)}>
          Save and Add Another Receipt
        </SubmitButton>
        <SubmitButton color="success" onClick={() => handleSubmit()}>
          Save
        </SubmitButton>
      </div>
    </LocalForm>
  </Page>
);

const initialState = {
  LineAmountTypes: "Inclusive",
  Total: 0,
  SubTotal: 0,
  TotalTax: 0,
  LineItems: [
    {
      key: shortid(),
      Quantity: 1,
      UnitAmount: 0,
      LineAmount: 0,
    },
  ],
};

export const enhance = compose(
  withReload(),
  withXeroErrors(),
  onMount(({ xero, match }) => () => {
    const { id } = match.params;
    return Promise.props({
      accounts: xero
        .get("accounts", {
          params: {
            raw: "ShowInExpenseClaims=true",
            ShowInExpenseClaims: true,
          },
        })
        .get("data")
        .map((i) => ({
          ...i,
          Description: `${i.Code} ${i.Description}`,
        })),
      users: xero
        .get("users")
        .get("data")
        .map((i) => ({
          ...i,
          Name: compact([i.FirstName, i.LastName]).join(" "),
        })),
      taxRates: xero
        .get("taxrates", {
          params: {
            raw: "CanApplyToExpenses=true",
          },
        })
        .get("data"),
      organisation: xero.get("organisations").get("data").get(0),
      initialState:
        id === "new"
          ? initialState
          : xero
              .get(`receipts/${id}`)
              .get("data")
              .then((i) => ({
                ...i,
                User: set(
                  i.User,
                  "Name",
                  compact([i.User.FirstName, i.User.LastName]).join(" ")
                ),
                LineItems: i.LineItems.map((j) => set(j, "key", shortid())),
              })),
    });
  }),
  withLocalForm({
    initialState: ({ initialState }) => initialState,
  }),
  withHandlers({
    handleSubmit:
      ({ xero, setXeroErrors, t, history, reload, dispatch }) =>
      (addAnother) => {
        dispatch((_, getState) => {
          const state = pick(getState().local, [
            "ReceiptID",
            "Date",
            "Contact",
            "LineItems",
            "User",
            "Reference",
            "LineAmountTypes",
          ]);
          state.LineItems = state.LineItems.map((i) =>
            pick(i, [
              "Description",
              "UnitAmount",
              "AccountCode",
              "Quantity",
              "TaxType",
              "LineAmount",
            ])
          );

          dispatch(
            actions.submit(
              "local",
              xero[state.ReceiptID ? "put" : "post"]("receipts", state).then(
                (resp) => {
                  NotificationManager.success(t("Saved successfully"));
                  history.replace(addAnother ? "new" : resp.data.ReceiptID);
                  reload();
                },
                setXeroErrors
              )
            )
          );
        });
      },
    recalculate:
      ({ dispatch, taxRates }) =>
      () => {
        dispatch((dispatch, getState) => {
          const state = getState().local;
          const LineItems = state.LineItems.map((i) => {
            const taxRate = find(taxRates, { TaxType: i.TaxType });
            const rate = taxRate ? taxRate.EffectiveRate / 100 : 0;
            const quantity = parseFloat(i.Quantity);
            const unitAmount = parseFloat(i.UnitAmount);
            const LineAmount =
              isNaN(quantity) || isNaN(unitAmount) ? 0 : quantity * unitAmount;
            let TaxAmount = 0;

            if (state.LineAmountTypes === "Inclusive") {
              TaxAmount = LineAmount - LineAmount / (1 + rate);
            } else if (state.LineAmountTypes === "Exclusive") {
              TaxAmount = LineAmount * rate;
            }

            return {
              LineAmount,
              TaxAmount,
            };
          });

          const SubTotal = sumBy(LineItems, "LineAmount");
          const TotalTax = sumBy(LineItems, "TaxAmount");

          const changes = flatten(
            {
              LineItems,
              SubTotal: SubTotal,
              TotalTax: TotalTax,
              Total:
                SubTotal +
                (state.LineAmountTypes === "Exclusive" ? TotalTax : 0),
            },
            "local"
          );

          forEach(changes, (v, m) => dispatch(actions.change(m, v)));
        });
      },
  }),
  withPropsOnChange(
    (props, nextProps) => {
      return deepDiff(props.state, nextProps.state);
    },
    ({ recalculate }) => {
      recalculate();
    }
  ),
  withContactSearch(),
  withLineAmountTypes(),
  withHandlers({
    handleRemoveLine:
      ({ dispatch }) =>
      (idx) =>
        dispatch(actions.remove("local.LineItems", idx)),
    handleChangeActionAndRecalculate:
      ({ dispatch, recalculate }) =>
      (m, v) => {
        dispatch(actions.change(m, v));
        recalculate();
      },
    handleAddNewLine:
      ({ dispatch }) =>
      () =>
        dispatch(
          actions.push("local.LineItems", {
            key: shortid(),
            Quantity: 1,
            UnitAmount: 0,
          })
        ),
    handleSaveAndAddAnother:
      ({ dispatch }) =>
      () => {
        dispatch(actions.submit("local"));
      },
  })
);

export default enhance(Edit);
